import type { SettingsPayload } from "../SettingsPayload";
import type { BaselineAssignments, PromptOverride } from "./toPromptOverrides";
import { toPromptOverrides } from "./toPromptOverrides";

/**
 * 引き継がれた上書きへ、手元の調整を重ねる。
 *
 * 上書きは再生成のたびに所有権ごと次のタスクへ移る器で、そこにはコンソールで直した本文が
 * 入っている。**アドインからは見えない調整なので、手元の値で丸ごと置き換えない。**
 *
 * ## skip だけは手元が正である
 * 引き継がれた上書きは写しより強い。したがって前回に一度でも本文を直した担当は、完了して
 * 内部用 PPTX に `skip: true` が焼き付いていても、引き継がれた `skip: false` が勝って再び
 * 実行される。アドインの画面は PPTX の値を出すので、**表示は「スキップ」なのに実行される**
 * という食い違いになる。
 *
 * 実行するかどうかは利用者がいま画面で見ている状態が正なので、`skip` は手元の値で上書きする。
 * 本文は引き継ぎを尊重し、手元に差分があるときだけ置き換える。
 *
 * ## 手元に無い担当は触らない
 * 手元の設定に現れない担当（コンソール側だけで足されたもの等）は、引き継がれた値のまま残す。
 */
export function mergePromptOverrides(
    carried: Record<string, PromptOverride>,
    payload: SettingsPayload,
    baseline: BaselineAssignments
): Record<string, PromptOverride> {
    const local = toPromptOverrides(payload, baseline);
    const merged: Record<string, PromptOverride> = { ...carried };
    for (const [id, assignment] of Object.entries(payload.assignments ?? {})) {
        const diff = local[id];
        if (diff) {
            merged[id] = diff;
            continue;
        }
        const kept = merged[id];
        if (kept) merged[id] = { ...kept, skip: assignment.skip === true };
    }
    return merged;
}
